// Helper central para permisos granulares de admin_type (session.admin_type.roles).
// session.admin_type.roles tiene la forma: [{ role: "access_<id>" }, { role: "can_<id>" }, ...]

// Roles del módulo de Órdenes (Panel → Roles → Tipos de administrador → Órdenes).
// "Acceso completo" y "Solo visualización" son mutuamente excluyentes (ver ModalAdminType.jsx).
export const ORDERS_FULL_ACCESS_URL = 'order-management';
export const ORDERS_READONLY_URL = 'orders-readonly';
export const ORDERS_FULL_ACCESS_ROLE = `access_${ORDERS_FULL_ACCESS_URL}`;
export const ORDERS_READONLY_ROLE = `access_${ORDERS_READONLY_URL}`;

// Cada vista de órdenes tiene su propio par de permisos "acceso completo" / "solo visualización".
// - type: coincide con orderType de NewTableOrders (null = Órdenes Generales).
// - fullUrl: primer segmento de la ruta de la vista y llave del permiso de acceso completo.
// - readonlyUrl: llave del permiso de solo visualización (no es una ruta).
// OJO: el backend guarda el rol ("access_" + llave) en un varchar(32), así que cada llave
// puede tener máximo 25 caracteres. Por eso las vistas nuevas usan llaves cortas.
export const ORDER_VIEWS = [
    { type: null, fullUrl: ORDERS_FULL_ACCESS_URL, readonlyUrl: ORDERS_READONLY_URL },
    { type: 'unpaid', fullUrl: 'orders-unpaid', readonlyUrl: 'orders-ro-unpaid' },
    { type: 'not-started', fullUrl: 'orders-not-started', readonlyUrl: 'orders-ro-not-started' },
    { type: 'unassigned', fullUrl: 'orders-unassigned', readonlyUrl: 'orders-ro-unassigned' },
    { type: 'cancelled', fullUrl: 'orders-cancelled', readonlyUrl: 'orders-ro-cancelled' },
];

export const ORDER_ROUTE_URLS = ORDER_VIEWS.map(view => view.fullUrl);
export const ORDERS_READONLY_URLS = ORDER_VIEWS.map(view => view.readonlyUrl);

// Mapa full <-> readonly de cada vista: activar uno desactiva el otro.
export const ORDERS_EXCLUSIVE_ROLES = ORDER_VIEWS.reduce((map, view) => {
    map[view.fullUrl] = view.readonlyUrl;
    map[view.readonlyUrl] = view.fullUrl;
    return map;
}, {});

// Mapa readonly -> ruta de la vista (ej: "orders-ro-unpaid" -> "orders-unpaid").
export const ORDERS_READONLY_TO_ROUTE = ORDER_VIEWS.reduce((map, view) => {
    map[view.readonlyUrl] = view.fullUrl;
    return map;
}, {});

// Devuelve la ruta a la que da acceso una llave de permiso (las de solo visualización
// apuntan a la ruta de su vista; el resto ya son la ruta).
export const getRouteUrl = (url) => ORDERS_READONLY_TO_ROUTE[url] || url;

// Primer segmento de la ruta del detalle abierto desde la lista de Órdenes Generales
// (/orders-general/:id). /order-management/:id lo comparten el resto de accesos al detalle
// (notificaciones, clientes, facturas, mapa...), así que no sirve para saber de qué vista viene.
// No es una llave de permiso: su acceso lo da el permiso de Órdenes Generales.
export const ORDERS_GENERAL_DETAILS_URL = 'orders-general';

// Primer segmento de la ruta del detalle de una orden según la vista desde la que se abre.
const getDetailsUrl = (view) => view.type ? view.fullUrl : ORDERS_GENERAL_DETAILS_URL;

// true si el primer segmento de la URL corresponde a alguna de las listas de órdenes
// (o al detalle abierto desde una de ellas).
export const isOrdersListPath = (segment) => ORDER_ROUTE_URLS.includes(segment) || segment === ORDERS_GENERAL_DETAILS_URL;

// Ruta del detalle de una orden abierta desde una lista de órdenes. Cada vista usa su propia
// ruta (ej: /orders-not-started/:id, /orders-general/:id) para que el detalle sepa de qué
// vista viene y aplique el permiso de esa vista.
export const getOrderDetailsPath = (orderType, orderId) => {
    const view = ORDER_VIEWS.find(v => v.type === (orderType || null)) || ORDER_VIEWS[0];
    return `/${getDetailsUrl(view)}/${orderId}`;
};

// Vista de la que viene un detalle de orden según su ruta (ej: /orders-unpaid/123 -> 'unpaid',
// /orders-general/123 -> null). Devuelve undefined si no se abrió desde una lista de órdenes
// (/order-management/:id, clientes, notificaciones, etc.), en cuyo caso aplica la regla
// general de canManageOrders.
export const getOrderDetailsViewType = (pathname) => {
    const segment = (pathname || '').split('/')[1];
    const view = ORDER_VIEWS.find(v => getDetailsUrl(v) === segment);
    return view ? view.type : undefined;
};

const getAdminTypeRoles = (session) => {
    return (session && session.admin_type && session.admin_type.roles) || [];
};

const hasRole = (session, role) => {
    return getAdminTypeRoles(session).some(r => r && r.role === role);
};

/**
 * Determina si el admin_type del usuario actual solo puede visualizar órdenes.
 *
 * - Con orderType (null = Órdenes Generales, 'unpaid', 'not-started', 'unassigned'): evalúa el
 *   par de permisos de esa vista (tiene el de solo visualización y no el de acceso completo).
 *   Aplica a la lista de esa vista y al detalle abierto desde ella (ver getOrderDetailsViewType).
 * - Sin orderType (detalle de orden, mapa, aside, etc. donde no se sabe de qué vista viene la
 *   orden): es solo lectura si tiene algún permiso de solo visualización y ninguno de acceso
 *   completo en ninguna vista.
 *
 * Los admin_types "legacy" sin roles granulares (filteredRoles vacío, sistema de permisos
 * hardcodeado por admin_type.id) siempre se consideran de acceso completo, preservando el
 * comportamiento actual para no romper admin_types ya existentes.
 */
const isOrdersReadOnly = (session, orderType) => {
    const roles = getAdminTypeRoles(session);
    const filteredRoles = roles.filter(r => r && r.role && r.role.startsWith('access'));

    if (filteredRoles.length === 0) return false;

    const isReadOnlyIn = (views) => {
        const hasFullAccess = views.some(view => filteredRoles.some(r => r.role === `access_${view.fullUrl}`));
        const hasReadOnlyAccess = views.some(view => filteredRoles.some(r => r.role === `access_${view.readonlyUrl}`));
        return hasReadOnlyAccess && !hasFullAccess;
    };

    // Si todos sus permisos de órdenes son de solo visualización, nunca puede modificar una
    // orden, sin importar desde dónde llegue.
    if (isReadOnlyIn(ORDER_VIEWS)) return true;
    if (orderType === undefined) return false;

    return isReadOnlyIn(ORDER_VIEWS.filter(view => view.type === (orderType || null)));
};

/**
 * Inverso de isOrdersReadOnly: true si el usuario puede ejecutar acciones de mutación
 * sobre órdenes (cambiar estado, cancelar, reasignar, editar, etc). Úsalo para gatear
 * botones/acciones en NewTableOrders (pasando su orderType), orderDetails, DefaultAside,
 * MapView, etc.
 */
const canManageOrders = (session, orderType) => !isOrdersReadOnly(session, orderType);

export default {
    getAdminTypeRoles,
    hasRole,
    isOrdersReadOnly,
    canManageOrders,
};



// WEBPACK FOOTER //
// ./src/utils/permissions.js